import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import {
  ScratchpadBucket,
  ScratchpadBucketScope,
  ScratchpadBucketType,
} from '../../../../../api/graphql/generated';
import { RightAlignedButtons } from '../../../../../components/button/aligned-buttons';
import { BaseButton } from '../../../../../components/button/base-button';
import { useScratchpadServiceContext } from '../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { FullScreenIonModal } from '../../../../../components/modal/full-screen-ion-modal';
import { ScratchpadBucketExplorer } from '../../../../../components/scratchpad-bucket/scratchpad-bucket-explorer';
import { ContentWithFooter } from '../../../../../components/ui/content-with-footer';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useFlowServices } from '../../hook/flow-service.hooks';
import { SystemUnit } from '../../model/flow.types';
import { ApplicationSettingsBucketEditModal } from './application-settings-bucket-edit-modal';

interface ApplicationSettingsSearchProps {
  title?: string;
  presentingElement?: HTMLElement | null;
  isOpen: boolean;
  isFullScreen?: boolean;
  onClose: () => void;
  onSelected: (bucket: ScratchpadBucket) => void;
  system: SystemUnit;
  showFooter?: boolean;
  implicitSearchTags: Record<string, unknown>;
  getBucketSubheading: (bucket: ScratchpadBucket) => string;
  type: ScratchpadBucketType;
}

const ApplicationSettingsSearch = (
  props: ApplicationSettingsSearchProps
): ReactElement => {
  const {
    title,
    isOpen,
    isFullScreen,
    onClose,
    onSelected,
    presentingElement,
    system,
    showFooter,
    implicitSearchTags,
    getBucketSubheading,
    type,
  } = props;

  const { scratchpad } = useScratchpadServiceContext();
  const [bucketId, setBucketId] = useState<string | undefined>();
  const explorerModalRef = useRef<HTMLIonModalElement>(null);
  const hasCalledOnClosed = useRef<boolean>(false);

  const { appSlug } = useFlowServices();

  const onCloseHandler = useCallback(() => {
    onClose();
    hasCalledOnClosed.current = true;
  }, [onClose]);

  const onSelectHandler = async (bucket: ScratchpadBucket) => {
    await onSelected(bucket);
    onCloseHandler();
  };

  const titleText = useMemo(
    () => title ?? getLocalizedText('common:select_from_library'),
    [title]
  );

  const footer = useMemo(() => {
    return (
      <>
        {showFooter && (
          <IonToolbar>
            <RightAlignedButtons>
              <BaseButton
                buttonProps={{
                  onClick: () => {
                    onCloseHandler();
                  },
                  color: 'primary',
                  fill: 'outline',
                }}
              >
                {getLocalizedText('common:enter_settings_manually')}
              </BaseButton>
            </RightAlignedButtons>
          </IonToolbar>
        )}
      </>
    );
  }, [onCloseHandler, showFooter]);

  return (
    <>
      <FullScreenIonModal
        isOpen={isOpen}
        isFullScreen={isFullScreen}
        ref={explorerModalRef}
        canDismiss={true}
        presentingElement={presentingElement ?? undefined}
        onWillPresent={() => {
          hasCalledOnClosed.current = false;
        }}
        onDidDismiss={() => {
          if (hasCalledOnClosed.current === false) {
            onCloseHandler();
          }
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot={'start'}>
              <IonButton
                onClick={() => {
                  onCloseHandler();
                }}
              >
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonTitle>{titleText}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <ContentWithFooter footer={footer}>
          <ScratchpadBucketExplorer
            parentId={scratchpad.machineId}
            scratchpadAppSlug={appSlug}
            searchPlaceholder={getLocalizedText(
              'common:library_explorer_placeholder'
            )}
            scope={ScratchpadBucketScope.MACHINE}
            onSelection={onSelectHandler}
            onViewBucket={(bucketId) => {
              setBucketId(bucketId);
            }}
            selectionLabel={getLocalizedText('common:use_settings')}
            viewLabel={getLocalizedText('common:view_settings')}
            editLabel={getLocalizedText('common:edit_settings')}
            system={system}
            implicitSearchTags={implicitSearchTags}
            getBucketSubheading={getBucketSubheading}
            type={type}
          />
        </ContentWithFooter>
      </FullScreenIonModal>
      <ApplicationSettingsBucketEditModal
        isOpen={bucketId !== undefined}
        isFullScreen={isFullScreen}
        presentingElement={explorerModalRef.current ?? undefined}
        onSave={async () => setBucketId(undefined)}
        onClose={() => setBucketId(undefined)}
        bucketId={bucketId}
        system={system}
        useDefaultParams={false}
      />
    </>
  );
};

export { ApplicationSettingsSearch };
